import ValidationHelper from 'legacy/lut/classes/helpers/partner360/ValidationHelper'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import {
  IFormHPContacts,
  IFormJBPHPContactRoles,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { ApplyButton } from 'legacy/lut/components/widgets/public/partner360/editors/ApplyButton'
import { DropdownAutocomplete } from 'legacy/lut/components/widgets/public/partner360/editors/DropdownAutocomplete'
import { Input } from 'legacy/lut/library/inputs'
import { MultiSelect } from 'legacy/lut/library/multiSelect'
import { ValidateInput } from 'legacy/lut/library/validation'
import { useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { IForm } from 'core-components/classes/models/IForm'
import { Checkbox } from 'core-components/library/checkbox'
import { Controller } from 'core-components/library/validation'
import { ValidationStatus } from 'core-components/library/validation'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { Static } from 'core-components/types'
import { Logic, Operator } from 'core-components/types'
import MeasureController from 'core/analytics/MeasureController'
import { getAppId } from 'core/classes/helpers/getAppId'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'

import styles from './HPContactsRequest.module.scss'

interface IProps {
  partnerId: string
  item: IObjectAny
  leadId: number
  roles: IFormJBPHPContactRoles[]
  onApply: (item: IForm) => Promise<void>
  metrics: MeasureController
  flexWidth: FlexDirectionBreak
}

export const HPContactsRequest = ({ roles, ...props }: IProps) => {
  const [item, setItem] = useState<IFormHPContacts | IObjectAny>(props.item)
  const [changed, setChanged] = useState(false)
  const [currStatus, setCurrStatus] = useState(ValidationStatus.Invalid)

  const userCrud = useMemo(() => {
    const crud = CrudStore.get(CrudStoreItem.AppUsersAppsSearch)
    crud.setDefaultFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'Id',
          operator: Operator.CONTAINS,
          value: '@hp.com',
        },
        {
          field: 'AppId',
          operator: Operator.EQUAL,
          value: getAppId(),
        },
      ],
    })
    return crud
  }, [])

  const staticRolesCrud = useMemo(() => {
    return new Crud<IFormJBPHPContactRoles>({
      fetcher: new Static(roles),
      keyField: 'UniqueId',
      defaultPaging: {
        page: 0,
        pageSize: roles.length,
      },
    })
  }, [roles])

  const onChange = (newValue: string | number, fieldName: string) => {
    setItem({ ...item, [fieldName]: newValue })
    !changed && setChanged(true)
  }

  const onChangeEmailItem = async (emailItem: IObjectAny) => {
    if (emailItem && emailItem['Id']) {
      setItem((prevItem) => ({
        ...prevItem,
        ManagerEmail: emailItem['Id'] as string,
        ManagerFirstName: emailItem['FirstName'] as string,
        ManagerLastName: emailItem['LastName'] as string,
      }))
    }
    setChanged(true)
  }

  return (
    <div className={styles.root}>
      <Controller
        onValidationStatusChange={({ status }) => {
          status !== 'WIP' && setCurrStatus(status)
        }}
      >
        <div className={styles.line}>
          <MultiSelect
            classes={{
              controlClasses: {
                root: styles.multiselect,
              },
            }}
            translation="jbp-hpcontactinfo-role"
            value={(item['Roles'] || '').split(', ')}
            source={staticRolesCrud}
            caption="Roles"
            maxListPageSize={10}
            onChange={(value) => onChange(value.filter((el) => el !== '').join(', '), 'Roles')}
          />
          <ValidateInput
            classes={{
              validatorClasses: {
                root: styles.input,
              },
            }}
            label="Email *"
            type="text"
            value={item['Email'] || ''}
            onChange={(value: string) => onChange(value, 'Email')}
            translation="jbp-hpcontactinfo-email-required"
            checkFn={(value) => ValidationHelper.checkEmail(value)}
          />
        </div>
        <div className={styles.line}>
          <Input
            classes={{
              root: styles.input,
            }}
            label="First_Name"
            type="text"
            value={item['First_Name'] || ''}
            onChange={(value: string) => onChange(value, 'First_Name')}
            translation="jbp-hpcontactinfo-firstname"
          />
          <Input
            classes={{
              root: styles.input,
            }}
            label="Last_Name"
            type="text"
            value={item['Last_Name'] || ''}
            onChange={(value: string) => onChange(value, 'Last_Name')}
            translation="jbp-hpcontactinfo-lastname"
          />
        </div>
        <div className={styles.line}>
          <DropdownAutocomplete
            label={'Email *'}
            fieldsToSearch={['UserAccountName']}
            classes={{
              input: styles.input,
              popPaper: styles.rolePaper,
            }}
            calculateCaption={(item: IObjectAny) =>
              `${item.FirstName} ${item.LastName} <${item.Id}>`
            }
            value={item.ManagerEmail || ''}
            source={userCrud}
            validateName="Id"
            checkFn={(value: string) => ValidationHelper.checkFullEmail(value)}
            onChange={(value: string) => onChange(value, 'ManagerEmail')}
            onItemChange={(value: IObjectAny) => onChangeEmailItem(value.ManagerEmail)}
            translation="jbp-hpcontactinfo-manager-email"
          />
          <Checkbox
            disabled={props.leadId !== -1 && props.leadId !== (item['EntryId'] || 0)}
            className={styles.input}
            label={<Translation text="jbp-hpcontactinfo-leadpbm" />}
            checked={!!item['LeadPBM']}
            onChange={() => onChange(item['LeadPBM'] ? 0 : 1, 'LeadPBM')}
          />
        </div>
        <div></div>
        <ApplyButton
          className={styles.applyButton}
          disabled={!changed || !item.Roles || currStatus !== ValidationStatus.Valid}
          onClick={async () => {
            setChanged(false)
            await props.onApply(item)
          }}
        />
      </Controller>
    </div>
  )
}
